<script setup lang="ts">
import moment from 'moment';
import { ref, onMounted } from 'vue';
import LogsMailRepository, { LogsMailFilters } from '../../repositories/logsmail.repository';
import { useToast } from '../../composables/useToast';
import { useI18n } from 'vue-i18n'
import DataTable from '@components/DataTable.vue';

interface Loading {
  status: boolean;
  itemId: number | null;
}

const { t } = useI18n();
const { $toast } = useToast();
const logsMailRepository = new LogsMailRepository();
const logsMailData = ref([]);
const loading = ref<Loading>({ status: false, itemId: null });
const isLoadingLogsMail = ref<boolean>(false);
const showModal = ref<boolean>(false);
const emailDetailHTML = ref<string>('');
const showSubject = ref<boolean>(false);
const showCC = ref<boolean>(false);
const paginationData = ref();
const currentPage = ref<number>();
const filters = ref<LogsMailFilters>({});
const searchFormRef = ref<any>(null);

// Evita que una respuesta lenta pise a una más nueva al filtrar/paginar rápido.
let lastRequestId = 0;

const getLogsMail = async (page: number) => {
  const requestId = ++lastRequestId;
  isLoadingLogsMail.value = true;
  try {
    const { data } = await logsMailRepository.getlogsMail({
      page,
      filters: filters.value,
    });
    if (requestId !== lastRequestId) return;
    logsMailData.value = data.logsMail ?? [];
    paginationData.value = data.pagination;
  } catch (error) {
    if (requestId === lastRequestId) $toast?.error(t('global.error'));
  } finally {
    if (requestId === lastRequestId) isLoadingLogsMail.value = false;
  }
};

onMounted(async () => {
  getLogsMail(1);
});

const dateInput = (key: 'startDate' | 'endDate', title: string) => ({
  key,
  title,
  type: 'datetime',
  enableTimePicker: false,
  format: 'dd/MM/yyyy',
  modelType: 'yyyy-MM-dd',
  autoApply: true,
  listenChange: true,
  onDatePicker: true,
  placeholder: title,
  value: null,
  min: null,
  cols: 2,
});

const fields: any = ref([
  {
    key: 'from',
    title: t('log_emails.de'),
    type: 'text',
    placeholder: t('log_emails.buscar_por_remitente'),
    value: null,
    cols: 2,
  },
  {
    key: 'to',
    title: t('log_emails.para'),
    type: 'text',
    placeholder: t('log_emails.buscar_por_destinatario'),
    value: null,
    cols: 2,
  },
  {
    key: 'subject',
    title: t('log_emails.asunto'),
    type: 'text',
    placeholder: t('log_emails.buscar_email_por_asunto'),
    value: null,
    cols: 4,
  },
  dateInput('startDate', t('log_emails.fecha_desde')),
  dateInput('endDate', t('log_emails.fecha_hasta')),
]);

// "Hasta" no puede ser anterior a "Desde".
const handleDateChange = async (e: any) => {
  if (e.input.key !== 'startDate') return;

  const endDateField = fields.value.find((field: any) => field.key === 'endDate');
  endDateField.min = e.value ? new Date(`${e.value}T00:00:00`) : null;

  const { items } = await searchFormRef.value.getValues();
  if (e.value && items.endDate && items.endDate < e.value) {
    searchFormRef.value.setValue({ key: 'endDate', value: null });
  }
};

const fieldsTable: any = ref([
  {
    key: 'id',
    label: t('global.id'),
    sortable: true,
  },
  {
    key: 'from',
    label: t('log_emails.de'),
  },
  {
    key: 'to',
    label: t('log_emails.para'),
  },
  {
    key: 'cc',
    label: 'CC',
  },
  {
    key: 'subject',
    label: t('log_emails.asunto'),
  },
  {
    key: 'sentAt',
    label: t('log_emails.fecha_de_envio'),
    formatter: (e: any) =>
      e ? moment(e).format('DD-MM-YYYY HH:mm') : 'Sin fecha',
  },
  {
    key: 'sentAt',
    label: t('global.estado'),
    formatter: (e: any) => (e ? 'Enviado' : 'No enviado'),
  },
  {
    key: 'error',
    label: t('global.error'),
    formatter: (e: any) => e ?? 'Sin Error',
  },
  {
    key: 'retry',
    label: t('log_emails.reintentos'),
  },
  {
    key: 'actions',
    label: t('global.acciones'),
  },
  {
    key: 'seeEmail',
    label: t('global.ver'),
  },
]);
const onChangePage = async (page: any) => {
  getLogsMail(page);
  currentPage.value = page;
};

const handleSendEmail = async ({ id }: { id: number }) => {
  try {
    loading.value = { status: true, itemId: id };
    await logsMailRepository.reSendEmail(id);
    $toast?.success(t('log_emails.email_reenviado_con_exito'));
  } catch (error) {
    $toast?.error(t('log_emails.ocurrio_un_error_al_reenviar_el_email'));
  } finally {
    getLogsMail(paginationData.value.current_page);
    loading.value = { status: false, itemId: null };
  }
};
const toggleShowSubject = () => {
  showSubject.value = !showSubject.value;
};
const toggleShowCC = () => {
  showCC.value = !showCC.value;
};
const handleOpenModal = (item: any) => {
  showModal.value = true;
  emailDetailHTML.value = item.body;
};
const handleCloseModal = () => {
  showModal.value = false;
  emailDetailHTML.value = '';
};
const handleOnSubmit = async ({ items }: any) => {
  filters.value = {
    from: items.from,
    to: items.to,
    subject: items.subject,
    startDate: items.startDate,
    endDate: items.endDate,
  };
  getLogsMail(1);
};
const handleReset = async () => {
  if (!searchFormRef.value) return;
  await searchFormRef.value.resetValues();
  const endDateField = fields.value.find((field: any) => field.key === 'endDate');
  endDateField.min = null;
  filters.value = {};
  getLogsMail(1);
};
</script>

<template>
  <Layout :title="t('log_emails.log_emails')" :textColor="'color-secondary'">
    <template #layout>
      <div class="form__logEmail">
        <Form
          ref="searchFormRef"
          @onSubmit="handleOnSubmit"
          @datePicker="handleDateChange"
          :inputs="fields"
          customClass="poliza__form"
        >
          <template #buttons>
            <CButton
              class="background-blue button__form"
              style="padding: 0.6rem !important; margin: 0.6rem"
              :disabled="isLoadingLogsMail"
            >
              <template #content> {{ t('global.buscar') }} </template>
            </CButton>
            <CButton
              class="background-blue button__form"
              style="padding: 0.6rem !important; margin: 0.6rem"
              type="button"
              @click="handleReset"
            >
              <template #content> {{ t('global.limpiar_filtro') }} </template>
            </CButton>
          </template>
        </Form>
      </div>
      <DataTable
        :loading="isLoadingLogsMail"
        :fields="fieldsTable"
        :items="logsMailData"
        @update:model-value-page="onChangePage"
        :totalRows="paginationData?.count"
        :perPage="20"
        :currentPage="paginationData?.current_page"
      >
        <template v-if="true" v-slot:cell(actions)="logsData">
          <div>
            <button
              class="background-pink p-1 border-0 w-100 text-white rounded-4"
              @click="handleSendEmail(logsData.data.item)"
              :disabled="
                loading.status && loading.itemId !== logsData.data.item.id
              "
            >
              <BSpinner
                v-if="
                  loading.status && loading.itemId === logsData.data.item.id
                "
                small="true"
              />
              <span v-else>{{ t('log_emails.reenviar_email') }}</span>
            </button>
          </div>
        </template>
        <template v-if="true" v-slot:cell(seeEmail)="logsData">
          <div>
            <button
              class="background-blue border-0 text-white rounded-4"
              @click="handleOpenModal(logsData.data.item)"
            >
              <img
                src="/icons/eye.svg"
                style="width: 30px; height: 30px; cursor: pointer"
              />
            </button>
          </div>
        </template>
        <template v-if="true" v-slot:cell(subject)="subject">
          <div class="d-flex align-self-center" @click="toggleShowSubject">
            <span
              :class="[showSubject ? '' : 'truncatedText']"
              :title="subject.data.value"
            >
              {{ subject.data.value }}
            </span>
          </div>
        </template>
        <template v-if="true" v-slot:cell(cc)="cc">
          <div class="d-flex align-self-center" @click="toggleShowCC">
            <span
              :class="[showCC ? '' : 'truncatedText']"
              :title="cc.data.value"
            >
              {{ cc.data.value }}
            </span>
          </div>
        </template>
      </DataTable>
      <BModal
        :modelValue="showModal"
        hide-footer
        :centered="true"
        @hide="handleCloseModal"
        title="Email"
        size="xl"
        static
      >
        <div class="modal-content">
          <div class="modal-body" style="overflow-y: auto">
            <div v-html="emailDetailHTML"></div>
          </div>
        </div>
      </BModal>
    </template>
  </Layout>
</template>

<style scoped>
.truncatedText {
  max-width: 15rem;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  cursor: pointer;
  margin: 0;
}
.form__logEmail {
  width: 100%;
}
</style>
